import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Grid, Typography, TextField as MUITextField } from '@material-ui/core';
import { Cancel, CheckCircle } from '@material-ui/icons';
import { useGenericStyles } from 'common/genericStyles';
import {
  TransferenciaInteroperabilidadeRequest,
  InvestidorResumo,
  PosicaoResponse,
  InstrumentoFinanceiroApiResponse,
  RegistroStatus,
} from 'common/genericTypes';
import UploadDocs, { Attachment } from 'components/uploadDocs';
import { Button, LgSpace, NumberField, Section, SmSpace, AutoComplete, TextField, MdSpace } from 'm2m.components';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { addCpfCnpjMask } from 'utils/mask';
import { formatNumber } from 'utils/number';
import { isNil, stripCpfCnpjFormatting } from 'utils/validation';
import _ from 'lodash';
import InstrumentoAutocomplete from 'components/operacoes/instrumentoAutocomplete';
import { getQuantityType } from 'utils/instrumentoFinanceiroUtils';

const SolicitarOperacaoInterop: React.FC = () => {
  const dispatch = useDispatch();

  const { getInstrumentoDetalhes, getInstrumentos: getInstrumentosApi } = useSelector(
    (state: RootState) => state.instrumentoApi
  );
  const { getPosicoes: getPosicoesApi } = useSelector((state: RootState) => state.investidorApi);
  const { postTransferenciaInteroperabilidade, putTransferenciaInteroperabilidadeDocuments } = useSelector(
    (state: RootState) => state.operacaoApi
  );
  const participanteId = useSelector((state: RootState) => state.auth.usuario.participante.id);

  const history = useHistory();
  const classes = useGenericStyles();

  const [posicoes, setPosicoes] = useState<Array<PosicaoResponse>>([]);
  const [instrumentos, setInstrumentos] = useState<Array<InstrumentoFinanceiroApiResponse>>([]);
  const [instrumentosValidos, setInstrumentosValidos] = useState<Array<InstrumentoFinanceiroApiResponse>>([]);
  const [investidores, setInvestidores] = useState<Array<InvestidorResumo>>([]);

  const [instrumentoInadimplido, setInstrumentoInadimplido] = useState<boolean>(false);
  const [quantidadeMax, setQuantidadeMax] = useState<number>(0);
  const [ticket, setTicket] = useState<string>('');

  const [instrumento, setInstrumento] = useState<InstrumentoFinanceiroApiResponse | null>(null);
  const [investidor, setInvestidor] = useState<InvestidorResumo | null>(null);
  const [quantidade, setQuantidade] = useState<number>(0);
  const [precoUnitario, setPrecoUnitario] = useState<number>(0);
  const [justificativa, setJustificativa] = useState<string>('');
  const [anexos, setAnexos] = useState<Array<Attachment>>([]);

  const getInstrumentos = useCallback(async () => {
    try {
      const result = await getInstrumentosApi(dispatch, { registroStatus: [RegistroStatus.Aprovado] }, true);
      setInstrumentos(result);
    } catch {}
  }, [dispatch, getInstrumentosApi]);

  const getPosicoes = useCallback(async () => {
    try {
      const result = await getPosicoesApi(dispatch, undefined, true);
      setPosicoes(result);
    } catch {}
  }, [dispatch, getPosicoesApi]);

  const getInstrumentoDetalhe = useCallback(async () => {
    try {
      if (instrumento) {
        const result = await getInstrumentoDetalhes(dispatch, instrumento.instrumentoFinanceiroGuid);
        setInstrumentoInadimplido(result?.hasPagamentosInadimplentes === true);
      } else setInstrumentoInadimplido(false);
    } catch (error) {}
  }, [instrumento, dispatch, getInstrumentoDetalhes]);

  useEffect(() => {
    getPosicoes();
    getInstrumentos();
  }, [getPosicoes, getInstrumentos]);

  useEffect(() => {
    const instrumentosResumo = _.uniqBy(
      posicoes.map((p) => p.instrumentoFinanceiro),
      (c) => c.id
    );
    setInstrumentosValidos(instrumentos.filter((i) => instrumentosResumo.some((ir) => ir.id === i.id)));

    const investidoresResumo = _.uniqBy(
      posicoes.map((p) => p.investidor),
      (c) => c.id
    );
    setInvestidores(investidoresResumo);
  }, [posicoes, instrumentos]);

  useEffect(() => {
    getInstrumentoDetalhe();
  }, [instrumento, getInstrumentoDetalhe]);

  useEffect(() => {
    if (instrumento && investidor) {
      const posicao = posicoes.find(
        (p) => p.instrumentoFinanceiro.id === instrumento.id && p.investidor.id === investidor.id
      );
      setQuantidadeMax(posicao ? posicao.saldoDisponivel : 0);
    } else setQuantidadeMax(0);
  }, [instrumento, investidor]);

  const handleSubmit = async () => {
    try {
      if (instrumento && investidor && quantidade && precoUnitario && justificativa) {
        const request: TransferenciaInteroperabilidadeRequest = {
          instrumentoFinanceiroId: instrumento.id,
          custodianteId: participanteId,
          investidorId: investidor.id,
          justificativa,
          precoUnitario,
          quantidade,
        };
        const files = anexos.map((a) => a.file!);
        const result = await postTransferenciaInteroperabilidade(dispatch, request, true);

        if (result.data.interoperabilidadeId) {
          if (files.length > 0)
            await putTransferenciaInteroperabilidadeDocuments(dispatch, result.data.interoperabilidadeId, files, true);
        }

        setTicket(result.data.ticketId);
      }
    } catch (error) {}
  };

  const formFulfilled = instrumento && investidor && quantidade && precoUnitario && justificativa;

  return (
    <div style={{ marginRight: 40 }}>
      <Typography variant="h1">{'Transferência Interoperabilidade'}</Typography>
      <LgSpace />
      {ticket ? (
        <Grid container direction="column" justify="center" alignItems="center">
          <div className={classes.iconWrapper}>
            <CheckCircle style={{ width: '40px', height: '40px' }} color="secondary" />
          </div>
          <SmSpace />
          <Typography variant="h3">{`Cadastro de transferência concluído com sucesso.`}</Typography>
          <SmSpace />
          <Typography variant="body2">{`Sua solicitação está sendo analisada pela área responsável. O código desta solicitação é ${ticket}.`}</Typography>
          <MdSpace />
          <Button onClick={() => history.push('/operacoes/gerenciar')} variant="contained" color="primary">
            {'Voltar para a página inicial'}
          </Button>
          <MdSpace />
        </Grid>
      ) : (
        <>
          {instrumentoInadimplido && (
            <>
              <SmSpace />
              <div className={classes.errorToast}>
                <Cancel style={{ marginRight: 10 }} />
                <div style={{ display: 'flex', flex: 1 }}>
                  <Typography variant="body2">
                    {
                      'Este Instrumento está Inadimplido, antes de continuar com a Movimentação solicite a ciência dos Custodiantes.'
                    }
                  </Typography>
                </div>
              </div>
            </>
          )}
          <Section title={'Detalhes da movimentação'} defaultGrid>
            <InstrumentoAutocomplete
              instrumentos={instrumentosValidos}
              instrumento={instrumento}
              onChange={setInstrumento}
            />
            <AutoComplete
              label={'CPF/CNPJ Investidor *'}
              options={investidores}
              getOptionLabel={(option: InvestidorResumo) => addCpfCnpjMask(option.cpfCnpj)}
              value={investidor}
              onChange={(newValue) => setInvestidor(newValue)}
              filterOptions={(options: Array<InvestidorResumo>, state) =>
                options.filter((_) => stripCpfCnpjFormatting(_.cpfCnpj).includes(stripCpfCnpjFormatting(state.inputValue)))
              }
              renderOption={(option: InvestidorResumo) => (
                <div style={{ width: '100%' }}>
                  <div style={{ display: 'flex' }}>
                    <Typography style={{ margin: 'auto 12px' }}>{addCpfCnpjMask(option.cpfCnpj)}</Typography>
                  </div>
                  <div>
                    <Typography style={{ margin: 'auto 12px' }} variant="body2">
                      {option.razaoSocial}
                    </Typography>
                  </div>
                </div>
              )}
              renderInput={(params) => (
                <MUITextField
                  {...params}
                  label={'CPF/CNPJ Investidor *'}
                  InputProps={{ ...params.InputProps }}
                  variant="outlined"
                />
              )}
              helperText={investidor?.razaoSocial}
            />
            <NumberField
              label="Quantidade"
              value={quantidade}
              onChange={(value) => setQuantidade(value ?? 0)}
              required
              disabled={!investidor}
              numberType={getQuantityType(instrumento?.tipoInstrumentoFinanceiro)}
              helperText={!isNil(quantidadeMax) ? `Quantidade disponível: ${formatNumber(quantidadeMax ?? 0, { minimumFractionDigits: 0, maximumFractionDigits: 8 })}` : ''}
            />
            <NumberField
              label="PU"
              value={precoUnitario}
              onChange={(value) => setPrecoUnitario(value ?? 0)}
              required
              numberType="eightDecimals"
            />
            <TextField
              label="Justificativa"
              value={justificativa}
              onChange={setJustificativa}
              required
              multiline
              fullWidth
              rows={4}
            />
          </Section>
          <Section title={'Anexos'}>
            <UploadDocs
              onChange={(atts) => setAnexos(anexos.concat(atts as Attachment[]))}
              onDelete={(attId) => setAnexos(anexos.filter((a) => a.id !== attId))}
              multipleFiles={true}
            />
          </Section>
          <Grid container justify="center">
            <Grid item>
              <Button variant="contained" color="primary" disabled={!formFulfilled} onClick={handleSubmit}>
                {'Solicitar Transferência'}
              </Button>
              <LgSpace />
            </Grid>
          </Grid>
        </>
      )}
    </div>
  );
};

export default SolicitarOperacaoInterop;
